import React, { useState } from 'react';
import { PNGS, SVGS } from '../../../assets';
import './waba-copy-short-link.scss';
import {
  getWorkspaceId,
  useAppSelector,
  useLazyGenerateEmbeddedSignupLinkQuery,
} from '@zonofi/seller-rtk-store';
import ZoButton from '@zonofi/zo-components/input/zo-button/zo-button';

interface WabaCopyShortLinkProps {
  onBackClick: () => void;
  onSkipClick: () => void;
}

export const WabaCopyShortLink: React.FC<WabaCopyShortLinkProps> = ({
  onBackClick,
  onSkipClick,
}) => {
  const [copied, setCopied] = useState(false);

  const [generateEmbeddedSignupLink, generateEmbeddedSignupLinkResponse] =
    useLazyGenerateEmbeddedSignupLinkQuery();
  const workspaceId = useAppSelector(getWorkspaceId);
  React.useEffect(() => {
    generateEmbeddedSignupLink({
      sellerWorkspaceId: workspaceId,
    });
  }, []);

  const onRegenerateLinkClick = () => {
    generateEmbeddedSignupLink({
      sellerWorkspaceId: workspaceId,
    });
  };

  // Function to handle copy functionality
  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(link);
      // Show copied notification
      setCopied(true);
      // Reset after 3 seconds
      setTimeout(() => {
        setCopied(false);
      }, 3000);
    } catch (error) {
      console.error('Error copying text:', error);
      alert('Failed to copy link. Please try manually copying it.');
    }
  };

  // Function to handle share functionality
  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'WABA Setup Link',
          text: 'Please open this link on a desktop browser to continue with WABA setup',
          url: link,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      alert(
        'Share functionality is not supported in this browser. You can copy the link manually.'
      );
    }
  };

  const link = generateEmbeddedSignupLinkResponse?.data?.esLink;
  const isLoading = generateEmbeddedSignupLinkResponse?.isLoading;

  return (
    <div className="waba-copy-short-link">
      <div className="waba-copy-short-link-container">
        <div className="waba-copy-short-link-container-header">
          <div className="waba-copy-short-link-container-header-back-icon primary-font-regular">
            <SVGS.ArrowLeftComponent onClick={onBackClick} fill="#000000" />{' '}
            Back
          </div>
        </div>
        <div className="waba-copy-short-link-container-body">
          <img
            src={SVGS.FacebookIcon}
            alt=""
            className="waba-copy-short-link-container-header-icon"
            width={93}
          />
          <div className="waba-copy-short-link-container-header-text">
            <span className="waba-copy-short-link-container-header-text-heading primary-font-bold">
              WABA signup is not supported in mobile browser.
            </span>
            <span className="waba-copy-short-link-container-header-text-description primary-font-regular">
              This action can't be completed in a mobile browser as per Facebook
              Guidelines, Please copy the link and open it on a desktop browser
              to continue
            </span>
          </div>
          <div className="waba-copy-short-link-container-header-link">
            <div className="embedded-signup-link-container" onClick={() => {}}>
              {isLoading ? (
                <span
                  className="embedded-signup-link regenerating-text"
                  style={{ textDecoration: 'underline' }}
                >
                  Regenerating link<span className="dot-animation">...</span>
                </span>
              ) : (
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="embedded-signup-link primary-font-regular"
                  style={{
                    textDecoration: 'underline',
                    color: '#000000',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                  onClick={() => {}}
                >
                  {link}
                  <div
                    className="waba-copy-short-link-container-header-link-icons"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <img
                      src={SVGS.CopyIcon}
                      alt=""
                      width={16}
                      height={16}
                      className="embedded-signup-icon"
                      style={{
                        marginLeft: '16px',
                        cursor: 'pointer',
                      }}
                      onClick={handleCopy}
                    />
                    <img
                      src={SVGS.ShareIcon}
                      alt=""
                      width={16}
                      height={16}
                      className="embedded-signup-icon"
                      style={{
                        marginLeft: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={handleShare}
                    />
                  </div>
                </a>
              )}
            </div>
            {copied && (
              <div
                className="notification-space primary-font-regular"
                style={{
                  height: '24px',
                  marginTop: '8px',
                  fontSize: '14px',
                }}
              >
                <div
                  style={{
                    color: '#32A7E8',
                  }}
                >
                  Link copied to clipboard!
                </div>
              </div>
            )}
            <div className="embedded-signup-description-2 primary-font-regular">
              If the link is not working,{' '}
              <span
                className="regenerate-link"
                onClick={onRegenerateLinkClick}
                style={{
                  color: '#32A7E8',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                click here
              </span>{' '}
              to regenerate the link.
            </div>
            <ZoButton
              variant="secondary"
              isLoading={false}
              onClick={onSkipClick}
              label={'I’ll do it Later'}
              style={{ border: '1px solid #32A7E8' }}
              className=""
            />
          </div>
        </div>
      </div>
    </div>
  );
};
